import React, { useEffect, useState } from 'react';
import { TbBrandWhatsapp, TbCaretUpFilled, TbCircleCheckFilled, TbLocationFilled, TbPhone } from 'react-icons/tb';
import { formatDistance, formatPhone } from '../../Utils/Formatters';
import { useStoreContext } from '../../Contexts/StoreContext';
import Badge from './Badge';
import { formatAddressStore, orderTypeAsSlug, orderTypeAsSlugConvert, storeIsOpenByOrderType } from '../../Utils/Helpers';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import Button from '../Form/Button';
import { ORDER_TYPE_LABELS } from '../../Utils/Constants/Service';
import Modal from './Modal';
import Confirm from './Confirm';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { getIconByStoreType } from '../../Utils/Constants/Global';
import { useTranslation } from 'react-i18next';

interface CardStoreListProps {
    store: any;
    onCardClick: (store: any) => void;
    enableCardClick?: boolean;
    showServices?: boolean;
    isCollapsed?: boolean;
    className?: string;
}

const CardStoreList = ({
    store,
    onCardClick,
    enableCardClick = true,
    showServices = false,
    isCollapsed = false,
    className
}: CardStoreListProps) => {

    const { t } = useTranslation();
    const { currentStore, setCurrentAddress } = useStoreContext();
    const { company } = useLayoutContext();
    const { orderType, visibleOrderType } = useOrderTypeContext();
    const [isOpen, setIsOpen] = useState(false);
    const isCurrent = currentStore && currentStore.id === store.id;
    const [ showConfirmStoreNavigation, setShowConfirmStoreNavigation ] = useState<boolean>(false);

    const [ nextCurrentOrderType, setNextCurrentOrderType ] = useState<any>(null);
    const [ showInfo, setShowInfo ] = useState<boolean>(isCollapsed);

    const activeServices = store.store_services?.filter((service: any) =>
        service.active
        && visibleOrderType.some((type: any) => type.id === service.order_type))
        || [];

    const storeChanged = currentStore && currentStore.id !== store.id;

    const hasOrderTypeChange = (orderTypeNameSelected: string) => {
        return orderTypeAsSlugConvert(orderTypeNameSelected) !== orderType;
    }

    const isCurrentOrderType = (orderTypeNameSelected: string) => {
        return orderTypeAsSlugConvert(orderTypeNameSelected) === orderType;
    }

    const hasStoreOrServiceChange = (orderTypeNameSelected: string) => {
        if (orderType && (storeChanged || hasOrderTypeChange(orderTypeNameSelected))) {
          setShowConfirmStoreNavigation(true);
          return true;
        }
        return false;
    };

    const handleClickService = (orderTypeSelected: any) => {
        setNextCurrentOrderType(orderTypeSelected);
        setTimeout(() => {
            if (hasStoreOrServiceChange(orderTypeSelected.order_type_name)) return;
            window.location.href = `/${orderTypeAsSlug(orderTypeSelected.order_type_name)}/${store.slug}`;
            handleStoreNavigation(orderTypeSelected.order_type_name);
        }, 100);
    }

    const handleStoreNavigation = (orderType: any) => {
        setCurrentAddress(null);
        window.location.href = `/${orderTypeAsSlug(orderType)}/${store.slug}`;
    }

    useEffect(() => {
        setIsOpen(storeIsOpenByOrderType(orderType, store));
    }, [orderType, store]);

    const getMessageChangeStoreOrService = () => {

        const isServiceChanged = nextCurrentOrderType && orderTypeAsSlugConvert(nextCurrentOrderType.order_type_name) !== orderType;

        const serviceLabelFrom = ORDER_TYPE_LABELS[orderType as keyof typeof ORDER_TYPE_LABELS];

        const serviceLabelTo = nextCurrentOrderType
            ? ORDER_TYPE_LABELS[orderTypeAsSlugConvert(nextCurrentOrderType.order_type_name) as keyof typeof ORDER_TYPE_LABELS]
            : '';

        const getChangeMessage = () => {
                if (storeChanged && isServiceChanged) {
                    return t('layout.store_list.message_change_store_or_service.store_and_service_changed');
                }

                if (storeChanged) {
                    return t('layout.store_list.message_change_store_or_service.store_changed');
                }

                if (isServiceChanged) {
                    return t('layout.store_list.message_change_store_or_service.service_changed');
                }

                return '';
            };

            const getStoreChangeInfo = () => {
                if (!storeChanged) return '';
                return `${currentStore.name} → <strong>${store.name}</strong>`;
            };

            const getServiceChangeInfo = () => {
                if (!isServiceChanged) return '';
                return `<strong>${serviceLabelFrom}</strong> → ${serviceLabelTo}`;
            };

            return `
                ${getChangeMessage()}
                ${getStoreChangeInfo()}
                ${(storeChanged && isServiceChanged) ? '<br />' : ''}
                ${getServiceChangeInfo()}
                <br /><br />
                ${t('layout.store_list.message_change_store_or_service.text_1')}
            `;
    };

    const handleToggleShowInfo = () => {
        setShowInfo(!showInfo);
    }

    return (
        <>
            <section className={`
                    relative flex flex-col w-full justify-between rounded-xl p-4 gap-4
                    ring-1 transition ease-in-out duration-200
                    bg-white shadow-md shadow-gray-100
                    ${isCurrent ? 'ring-2 ring-custom-primary' : 'ring-gray-100'}
                    ${enableCardClick && 'cursor-pointer hover:text-custom-primary active:ring-2 active:ring-custom-primary active:shadow-lg md:hover:shadow-lg'}
                    ${className}
                `}
                onClick={() => enableCardClick ? onCardClick(store) : {}}
                >
                {isCurrent &&
                    <TbCircleCheckFilled size={28} className='absolute -right-2 -top-2 bg-white rounded-full text-custom-primary zoomInIconNoDelay' />
                }

                <div className='flex flex-row gap-3'>
                    <div className='self-start'>
                        {company &&
                            <div className={`flex items-center justify-center h-full w-12 transition-all ease-in-out ${!isOpen && 'opacity-30 grayscale'}`}>
                                <img src={company.vector_image ?? company.image} alt={company.name} className={`
                                    h-full object-contain
                                    transition-[width] ease-in-out duration-200
                                `} />
                            </div>
                        }
                    </div>

                    <div className='grid text-left w-full gap-1.5 text-sm'>

                        <span className={`flex justify-between text-base font-semibold leading-5`}>
                            <span className='leading-none mb-1'>{store.name}</span>
                            <Badge className={`text-xs normal-case self-start font-semibold bg-transparent ${isOpen ? 'text-shopfood-success' : 'text-shopfood-error'}`}>{isOpen ? 'Aberta' : 'Fechada'}</Badge>
                        </span>

                        {!showInfo &&
                            <>
                                <span className='text-gray-500 text-sm leading-none'>
                                    { formatAddressStore(store) }
                                </span>

                                {store.distance &&
                                    <div className='flex items-center flex-wrap gap-x-0.5 text-nowrap font-medium text-gray-500'>
                                        <TbLocationFilled /> { formatDistance(store.distance) } de distância
                                    </div>
                                }

                                {(store.phone || store.phone_additional || store.whatsapp_number) &&
                                    <div className='flex items-center flex-wrap gap-x-2 text-gray-500'>
                                        {store.phone && (store.phone !== store.whatsapp_number) && <span className='flex gap-0.5 items-center text-nowrap'><TbPhone /> { formatPhone(store.phone) }</span>}
                                        { store.phone_additional && <span className='flex gap-0.5 items-center text-nowrap'><TbPhone /> { formatPhone(store.phone_additional) }</span>  }
                                        { store.whatsapp_number && <span className='flex gap-0.5 items-center text-nowrap'><TbBrandWhatsapp /> { formatPhone(store.whatsapp_number) }</span> }
                                    </div>
                                }
                            </>
                        }

                        {store.types && store.types.length > 0 &&
                            <div className='flex items-center flex-wrap gap-1 opacity-75 mb-2'>
                                {store?.types.map((type: any, index: number) =>
                                    <Badge key={index} outline className='text-xs items-center gap-0.5 normal-case font-medium leading-4'>{getIconByStoreType(type.type,  12)} { type.name }</Badge>
                                )}
                            </div>
                        }

                        {(showServices && activeServices?.length > 0) &&
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {activeServices.map((service: any) => {
                                    return (
                                        <Button
                                            key={service.id}
                                            onClick={() => handleClickService(service)}
                                            className="relative h-7"
                                            >
                                            <span className="text-xs leading-none text-nowrap">
                                                {service.order_type_name_custom}
                                            </span>
                                            {isCurrent && isCurrentOrderType(service.order_type_name) &&
                                                <TbCircleCheckFilled size={15} className='absolute -right-1 -top-0.5 rounded-full zoomInIconNoDelay bg-white text-custom-primary' />
                                            }
                                        </Button>
                                    )}
                                )}
                            </div>
                        }

                        {isCollapsed &&
                            <button onClick={handleToggleShowInfo} className={`flex items-center gap-0.5 h-full text-xs leading-none font-medium text-nowrap text-gray-400`}>
                                {showInfo ? t('layout.store_list.show_info_label') : t('layout.store_list.hide_info_label')}
                                <span><TbCaretUpFilled className={`transition-transform duration-300 ${showInfo ? '-rotate-180' : ''}`} /></span>
                            </button>
                        }

                    </div>
                </div>

            </section>


            <Modal open={showConfirmStoreNavigation} className="max-w-md m-4" onClose={() => setShowConfirmStoreNavigation(false)}>
                <Confirm
                    onConfirm={() => handleStoreNavigation(nextCurrentOrderType.order_type_name)}
                    onCancel={() => setShowConfirmStoreNavigation(false)}
                    title={t('layout.store_list.confirm_change_title')}
                    message={getMessageChangeStoreOrService()}
                    labelConfirm={t('layout.store_list.confirm_button_label')}
                />
            </Modal>
        </>
    );
}

export default CardStoreList;
